import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, Loader2, User, Bot } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { mentorDialogsAPI, MentorDialogListItem, MentorDialogTurn } from '../services/api';

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const PERSONA_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Все' },
  { value: 'anna', label: 'Анна (HR)' },
  { value: 'oleg', label: 'Олег (тех)' },
  { value: 'ai_advice', label: 'Советы ИИ' },
];

function personaBadgeClass(persona: string): string {
  switch (persona) {
    case 'anna': return 'bg-purple-100 text-purple-800';
    case 'oleg': return 'bg-amber-100 text-amber-800';
    case 'ai_advice': return 'bg-emerald-100 text-emerald-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}

const MentorDialogsPage: React.FC = () => {
  const [persona, setPersona] = useState('');
  const [list, setList] = useState<MentorDialogListItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState<MentorDialogListItem | null>(null);
  const [turns, setTurns] = useState<MentorDialogTurn[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async (p: string, page = 1, append = false) => {
    try {
      setLoadingList(true);
      const data = await mentorDialogsAPI.list(p, page, 100);
      if (data.success) {
        setList((prev) => (append ? [...prev, ...(data.dialogs || [])] : data.dialogs || []));
        setPagination(data.pagination);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить диалоги');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    setSelected(null);
    setTurns([]);
    loadList(persona, 1, false);
  }, [persona, loadList]);

  const selectDialog = useCallback(async (row: MentorDialogListItem) => {
    setSelected(row);
    setDetailLoading(true);
    setTurns([]);
    try {
      const data = await mentorDialogsAPI.getConversation(row.conversationId, row.persona);
      if (data.success) setTurns(data.turns || []);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Ошибка загрузки диалога');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle className="h-8 w-8 text-blue-600" />
          Наставник
        </h1>
        <p className="text-gray-600 mt-2">Диалоги пользователей с наставниками (Олег, Анна) и Советами ИИ</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PERSONA_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setPersona(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
              persona === f.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-16rem)] lg:items-stretch">
        {/* List */}
        <div className="w-full lg:w-1/2 lg:min-w-0 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-sm text-gray-500">
            Всего диалогов: {pagination.total}
          </div>
          <div className="overflow-auto flex-1 max-h-[calc(100vh-18rem)]">
            {loadingList ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
            ) : list.length === 0 ? (
              <p className="p-4 text-gray-500">Нет диалогов</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {list.map((row) => (
                  <li
                    key={`${row.persona}-${row.conversationId}`}
                    onClick={() => selectDialog(row)}
                    className={`cursor-pointer px-3 py-2.5 hover:bg-gray-50 ${
                      selected?.conversationId === row.conversationId ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${personaBadgeClass(row.persona)}`}>
                        {row.personaLabel}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{formatDt(row.lastActivityAt)}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-gray-800 truncate" title={row.userFullName}>
                      {row.userFullName || '—'} <span className="text-gray-400 font-normal">· {row.turns} сообщ.</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate" title={row.firstMessage}>{row.firstMessage}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {pagination.pages > 1 && pagination.page < pagination.pages && (
            <div className="p-2 border-t border-gray-100">
              <button type="button" onClick={() => loadList(persona, pagination.page + 1, true)} className="text-sm text-blue-600 hover:underline">
                Загрузить ещё (стр. {pagination.page + 1})
              </button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="w-full lg:w-1/2 lg:min-w-0 flex flex-col border border-gray-200 rounded-lg bg-white min-h-[24rem]">
          <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-600">
            {selected ? `${selected.personaLabel} · ${selected.userFullName || '—'}` : 'Выберите диалог'}
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-3 sm:p-4 space-y-2 bg-slate-50">
            {detailLoading && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}
            {!detailLoading && !selected && (
              <div className="flex h-full items-center justify-center text-gray-500 text-center px-6">Выберите диалог из списка слева</div>
            )}
            {!detailLoading && selected && turns.length === 0 && (
              <p className="text-gray-500 text-sm">Сообщений нет</p>
            )}
            {!detailLoading && turns.map((t) => (
              <div key={t.id} className="space-y-2">
                <div className="flex justify-end">
                  <div className="max-w-[90%] rounded-lg px-3 py-2 text-sm bg-blue-600 text-white">
                    <div className="flex items-center gap-1 text-xs opacity-80 mb-1"><User className="h-3 w-3" /><span>Пользователь</span><span className="ml-1">{formatDt(t.createdAt)}</span></div>
                    <div className="whitespace-pre-wrap break-words">{t.userMessage}</div>
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="max-w-[90%] rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-900">
                    <div className="flex items-center gap-1 text-xs opacity-80 mb-1"><Bot className="h-3 w-3" /><span>Наставник</span>{t.model && <span className="ml-1 text-gray-400">{t.model}</span>}</div>
                    <div className="whitespace-pre-wrap break-words">{t.responseText}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorDialogsPage;
